import { ref, nextTick } from 'vue';
import { debounce } from 'lodash';
import { useTransformsStore } from '@/stores/transforms.store';

/**
 * Composable for centralized transform implementation save orchestration
 * Eliminates dual save paths by managing all save operations in parent component
 */
export function useTransformSaveOrchestrator(transformId) {
    const transformsStore = useTransformsStore();

    // Centralized save state
    const isSaving = ref(false);
    const saveError = ref(null);
    const hasUnsavedChanges = ref(false);
    const implementationCurrentVersion = ref(null);

    // Data loading state
    const isLoading = ref(false);
    const loadError = ref(null);
    const transform = ref(null);
    const implementationHistory = ref([]);
    const transformObjective = ref('');
    const originalObjective = ref('');

    // Objective save state
    const savingObjective = ref(false);

    // Auto-save configuration
    const autoSaveEnabled = ref(true);
    const autoSaveDebounceMs = ref(2000);

    /**
     * Load transform and implementation data
     * Single source of truth for all data fetching
     */
    const loadTransformData = async () => {
        if (!transformId) {
            console.error('Transform ID is required');
            return null;
        }

        isLoading.value = true;
        loadError.value = null;
        
        try {
            // Fetch transform data
            const transformData = await transformsStore.getById(transformId, true);
            transform.value = transformData;

            if (transformData?.id) {
                // Fetch implementation data
                const implementationData = await transformsStore.getImplementationDetails(transformData.id);
                const implementation_versions = implementationData?.impl_versions ?? {};
                implementationHistory.value = implementation_versions;

                // Get latest version (numeric-aware, e.g., '10' > '2')
                const versionKeys = Object.keys(implementation_versions);
                const latestKey = versionKeys
                  .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }))
                  .pop();
                const latestImplementation = latestKey ? implementation_versions[latestKey] : null;

                if (latestImplementation) {
                    implementationCurrentVersion.value = latestImplementation;
                    hasUnsavedChanges.value = false;
                    saveError.value = null;
                }

                // Initialize objective from implementation description
                if (implementationData?.description) {
                    transformObjective.value = implementationData.description;
                    originalObjective.value = implementationData.description;
                }
            }

            return transform.value;
        } catch (error) {
            console.error('Error loading transform data:', error);
            loadError.value = error;
            throw error;
        } finally {
            isLoading.value = false;
        }
    };

    /**
     * Save transform objective/description
     */
    const saveTransformObjective = async () => {
        if (!transform.value?.id) {
            console.error('Transform ID not available');
            return;
        }

        savingObjective.value = true;
        try {
            const implementationDetailsToSave = {
                transform_id: transform.value.id,
                description: transformObjective.value
            };

            await transformsStore.editImplementationDetails(transform.value.id, implementationDetailsToSave);
            
            // Update the original value to reflect the saved state
            originalObjective.value = transformObjective.value;
        } catch (e) {
            console.error('Error saving transform objective', e);
            throw e;
        } finally {
            savingObjective.value = false;
        }
    };

    /**
     * Centralized save implementation function
     * Handles all save operations regardless of language type
     */
    const saveImplementation = async (customImplementation = null) => {
        if (!transformId || !implementationCurrentVersion.value) {
            console.error('Transform ID or implementation not available');
            return null;
        }

        isSaving.value = true;
        saveError.value = null;

        try {
            const implementationToSave = customImplementation || implementationCurrentVersion.value;

            // Filter out null/empty description to avoid sending unnecessary data
            const cleanImplementation = { ...implementationToSave };
            if (!cleanImplementation.description || cleanImplementation.description.trim() === '') {
                delete cleanImplementation.description;
            }

            const savedImplDetails = await transformsStore.editImplementationDetails(transformId, cleanImplementation);

            // Update with server response
            implementationCurrentVersion.value = savedImplDetails;
            hasUnsavedChanges.value = false;

            return savedImplDetails;
        } catch (e) {
            console.error('Error saving implementation', e);
            // Handle all save errors locally - display in UI for user to see and fix
            await nextTick();
            saveError.value = e;
            // DO NOT clear unsaved states - keep content and unsaved state
            throw e;
        } finally {
            isSaving.value = false;
        }
    };

    /**
     * Handle content changes from child components
     * This is the single entry point for all content updates
     */
    const handleContentChange = (updatedImplementation) => {
        if (!implementationCurrentVersion.value?.implementation) {
            console.warn('No implementationCurrentVersion available to update');
            return;
        }

        // Update the implementation content
        implementationCurrentVersion.value.implementation = updatedImplementation;
        hasUnsavedChanges.value = true;

        // Clear previous errors when user makes changes
        if (saveError.value) {
            saveError.value = null;
        }

        // Trigger auto-save if enabled
        if (autoSaveEnabled.value && saveDebounced) {
            saveDebounced();
        }
    };

    /**
     * Handle "show example" clicks
     * Updates content but allows user to save manually
     */
    const handleShowExample = (exampleImplementation) => {
        if (!implementationCurrentVersion.value?.implementation) {
            console.warn('No implementationCurrentVersion available to update');
            return;
        }

        // Update content from template/example
        implementationCurrentVersion.value.implementation = exampleImplementation;
        hasUnsavedChanges.value = true;

        // Clear errors when loading example
        if (saveError.value) {
            saveError.value = null;
        }

        // Note: No auto-save on show example - user must manually save
    };

    /**
     * Manual save trigger (for save buttons)
     */
    const handleManualSave = async () => {
        try {
            const result = await saveImplementation();
            return result;
        } catch (e) {
            // Error already handled in saveImplementation
            return null;
        }
    };

    // Debounced auto-save function
    const saveDebounced = debounce(async () => {
        if (!hasUnsavedChanges.value || isSaving.value) return;

        try {
            await saveImplementation();
        } catch (e) {
            // Error already handled in saveImplementation
        }
    }, autoSaveDebounceMs.value);

    /**
     * Initialize implementation data
     */
    const initializeImplementation = (initialImplementation) => {
        implementationCurrentVersion.value = initialImplementation;
        hasUnsavedChanges.value = false;
        saveError.value = null;
    };

    /**
     * Reset error state
     */
    const clearSaveError = () => {
        saveError.value = null;
    };

    /**
     * Set auto-save configuration
     */
    const configureAutoSave = (enabled = true, debounceMs = 2000) => {
        autoSaveEnabled.value = enabled;
        autoSaveDebounceMs.value = debounceMs;
    };

    return {
        // Loading state
        isLoading,
        loadError,
        
        // Data state
        transform,
        implementationHistory,
        transformObjective,
        originalObjective,
        implementationCurrentVersion,
        
        // Save state
        isSaving,
        saveError,
        hasUnsavedChanges,
        savingObjective,

        // Methods
        loadTransformData,
        saveTransformObjective,
        saveImplementation,
        handleContentChange,
        handleShowExample,
        handleManualSave,
        initializeImplementation,
        clearSaveError,
        configureAutoSave
    };
}
